import { setGenderPreference, shouldShowGenderPreferenceModal, setStylePreference, setAgeContentPrefs } from '../utils/userPrefs.js';
import { isSfwSite } from '../config.js';
import { showUnrestrictedBridge } from './sfwBridge.js';
import { getCurrentLanguage } from '../translations.js';
import { storage } from '../utils/storage.js';

const loadCSS = (href) => new Promise((resolve) => {
  try {
    if (document.querySelector(`link[href="${href}"]`)) return resolve(true);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(true);
    link.onerror = () => resolve(false);
    document.head.appendChild(link);
  } catch { resolve(false); }
});

// Anime preview images. Realism toggle/assets will return when more realism characters exist.
const STYLE_OPTION_IMAGES = {
  female: '/assets/images/characters/kiss_bikini.webp',
  male: '/assets/images/characters/enzo_swimsuit.webp'
};

/**
 * Gender Preference Modal for unlogged users
 * Shows on first visit to rankings page to set gender and style preference
 */
export class GenderPreferenceModal {
  constructor() {
    this.modal = null;
    this.onPreferenceSelected = null;
    this.language = getCurrentLanguage();
    this.translations = null;
    this.isVisible = false;
    this.keydownHandler = null;
    this.beforeUnloadHandler = null;
    this.pageHideHandler = null;
    this.navigationClickHandler = null;
    this.didFinalize = false;
  }

  /**
   * Show the gender preference modal
   * @param {Function} callback - Called with (isMalePreference): true for male, false for female
   */
  async show(callback) {
    this.onPreferenceSelected = callback;
    this.didFinalize = false;
    await loadCSS('/css/modals/gender-preference-modal.css');
    await this.loadTranslations();
    this.createModal();
    this.modal.style.display = 'flex';
    this.isVisible = true;
    this.attachLifecycleListeners();

    // Add fade-in animation
    requestAnimationFrame(() => {
      this.modal.classList.add('show');
    });
  }

  async loadTranslations() {
    try {
      const response = await fetch(`/translations/gender-preference-modal-${this.language}.json`);
      if (response.ok) {
        this.translations = await response.json();
      } else {
        // Fallback to English if translation file not found
        const fallbackResponse = await fetch('/translations/gender-preference-modal-en.json');
        this.translations = await fallbackResponse.json();
      }
    } catch (error) {
      console.error('Error loading translations for gender preference modal:', error);
      // Fallback to English on error
      const fallbackResponse = await fetch('/translations/gender-preference-modal-en.json');
      this.translations = await fallbackResponse.json();
    }
  }

  createModal() {
    // Remove existing modal if any
    const existingModal = document.getElementById('gender-preference-modal');
    if (existingModal) {
      existingModal.remove();
    }

    this.modal = document.createElement('div');
    this.modal.id = 'gender-preference-modal';
    this.modal.className = 'gender-preference-modal';

    this.modal.innerHTML = `
      <div class="gender-preference-modal-content">
        <div class="gender-preference-header">
          <p class="gender-preference-subtitle">${this.translations.subtitle}</p>
        </div>

        <div class="gender-preference-options">
          <button type="button" class="gender-preference-option female-option selected" data-preference="female" aria-pressed="true">
            <img class="option-icon" src="${STYLE_OPTION_IMAGES.female}" alt="Female character" />
            <div class="option-text">
              <h3>${this.translations.female_option_title}</h3>
            </div>
          </button>
          
          <button type="button" class="gender-preference-option male-option" data-preference="male" aria-pressed="false">
            <img class="option-icon" src="${STYLE_OPTION_IMAGES.male}" alt="Male character" />
            <div class="option-text">
              <h3>${this.translations.male_option_title}</h3>
            </div>
          </button>
        </div>
        
        <div class="age-confirm-container">
          <button type="button" class="age-confirm-btn age-confirm-btn-secondary" id="age-confirm-under-18" data-over18="false">
            ${this.translations.age_confirm_under_18}
          </button>
          <button type="button" class="age-confirm-btn age-confirm-btn-primary" id="age-confirm-18-plus" data-over18="true">
            ${this.translations.age_confirm_18_plus}
          </button>
        </div>
        <div class="gender-preference-footer">
          <p class="preference-note">${this.translations.footer_note}</p>
        </div>
      </div>
    `;

    // Add event listeners
    this.addEventListeners();

    document.body.appendChild(this.modal);
  }

  addEventListeners() {
    const options = this.modal.querySelectorAll('.gender-preference-option');
    const ageButtons = this.modal.querySelectorAll('.age-confirm-btn');
    let selectedGender = false;
    /** @type {boolean | null} */
    let over18Choice = null;

    const finalizeSelection = async (isMale, over18) => {
      this.didFinalize = true;
      setGenderPreference(isMale);
      setStylePreference('anime');
      const nsfw = isSfwSite() ? false : over18;
      await setAgeContentPrefs({ over18, nsfw, skipScroll: true });

      document.dispatchEvent(new CustomEvent('stylePreferenceSelected', {
        detail: { style: 'anime', skipScroll: true }
      }));
      document.dispatchEvent(new CustomEvent('genderPreferenceSelected', {
        detail: { isMale, skipScroll: true }
      }));

      if (this.onPreferenceSelected) {
        this.onPreferenceSelected(isMale, { over18, nsfw, dismissed: false });
      }
      if (isSfwSite() && over18) showUnrestrictedBridge();
      this.hide({
        closeReason: 'option_select',
        selectedPreference: isMale ? 'male' : 'female'
      });
    };

    const hasAgeChoice = () => over18Choice !== null;

    const checkAndFinalize = () => {
      if (hasAgeChoice() && selectedGender !== null) {
        finalizeSelection(selectedGender, over18Choice);
      }
    };

    ageButtons.forEach((button) => {
      button.addEventListener('click', () => {
        over18Choice = button.dataset.over18 === 'true';
        if (isSfwSite() && over18Choice) showUnrestrictedBridge();
        checkAndFinalize();
      });
    });

    options.forEach(option => {
      option.addEventListener('click', () => {
        options.forEach((opt) => {
          opt.classList.remove('selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        option.classList.add('selected');
        option.setAttribute('aria-pressed', 'true');

        const preference = option.dataset.preference;
        selectedGender = preference === 'male';
        checkAndFinalize();
      });
    });

  }

  // Show mobile toast notification
  async showMobileToast() {
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      try {
        const { showInfo } = await import('../utils/UnifiedNotificationSystem.js');
        showInfo(this.translations.mobile_toast, {
          duration: 8000,
          position: 'top-center',
          icon: 'mobile-alt'
        });
      } catch (error) {
        console.warn('Could not load notification system:', error);
      }
    }
  }

  attachLifecycleListeners() {
    this.keydownHandler = (event) => {
      if (event.key !== 'Escape' || !this.modal || !this.isVisible) return;
      this.hide({ closeReason: 'esc' });
    };

    this.beforeUnloadHandler = () => {
      this.hide({ closeReason: 'unload', immediate: true });
    };

    this.pageHideHandler = () => {
      this.hide({ closeReason: 'unload', immediate: true });
    };

    this.navigationClickHandler = (event) => {
      if (!this.isVisible) return;
      const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
      if (!target) return;
      const anchor = target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:')) return;
      this.hide({ closeReason: 'navigation', immediate: true });
    };

    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('click', this.navigationClickHandler, true);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  detachLifecycleListeners() {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    if (this.navigationClickHandler) {
      document.removeEventListener('click', this.navigationClickHandler, true);
      this.navigationClickHandler = null;
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
  }

  hide(options = {}) {
    if (!this.modal) return;
    const immediate = options.immediate === true;

    if (!this.didFinalize && this.onPreferenceSelected) {
      this.onPreferenceSelected(null, { over18: false, nsfw: false, dismissed: true });
      this.onPreferenceSelected = null;
    }

    if (this.isVisible) {
      this.isVisible = false;
    }

    this.detachLifecycleListeners();

    if (immediate) {
      if (this.modal.parentNode) this.modal.remove();
      this.modal = null;
      return;
    }

    this.modal.classList.remove('show');

    setTimeout(() => {
      if (this.modal && this.modal.parentNode) {
        this.modal.remove();
      }
      this.modal = null;
    }, 300);
  }

  /**
   * Check if user should see the gender preference modal
   * @returns {boolean} True if modal should be shown
   */
  static shouldShow() {
    return shouldShowGenderPreferenceModal();
  }

  /**
   * Get the current gender preference
   * @returns {boolean} True for male, false for female, null if not set
   */
  static getCurrentPreference() {
    // Check saved preference
    const savedPreference = storage.get('gender_preference');
    if (savedPreference !== null) {
      return savedPreference === 'true';
    }

    // Check user data
    try {
      const userData = storage.get('userData') || {};
      if (userData && userData.gender_preference !== undefined) {
        if (typeof userData.gender_preference === 'boolean') {
          return userData.gender_preference;
        }
        return userData.gender_preference === 'male_only';
      }
    } catch (error) {
      // Return null if can't parse
    }

    return null;
  }
}
